import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Star, Send, Loader2, X, Clock, Sparkles, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMentorSignupEnabled } from '../hooks/useMentorSignupEnabled';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import posthog from '../lib/posthog';
import MentorBadge from './MentorBadge';

const MIN_CHARS = 50;
const MAX_CHARS = 200;

/* ── Mentor Message Modal ── */
const MentorMessageModal = ({ mentor, onClose, onSent }) => {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const textareaRef = useRef(null);
  const headingId = 'mentor-msg-heading';

  // Focus trap
  useEffect(() => {
    const el = textareaRef.current;
    if (el) el.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const focusable = document.querySelectorAll('[data-mentor-modal] button, [data-mentor-modal] textarea');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSend = async () => {
    if (!content.trim()) { setError('Please write a message.'); return; }
    setSending(true);
    setError('');
    try {
      await api.sendMentorMessage(mentor.id, content.trim());
      posthog.capture('mentor_message_sent');
      setSent(true);
      setTimeout(() => { onSent(); onClose(); }, 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      data-mentor-modal
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className="w-full max-w-lg rounded-arch-xl overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between gap-4" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <img
              src={mentor.avatar_url || defaultAvatar(mentor.gender)}
              alt={mentor.display_name}
              className="w-10 h-10 rounded-full object-cover border-2"
              style={{ borderColor: 'var(--primary)' }}
            />
            <div>
              <h2 id={headingId} className="font-heading text-base font-semibold text-text-main flex items-center gap-2">
                {mentor.display_name}
                <MentorBadge show withLabel={false} size={12} />
              </h2>
              {mentor.mentor_specialty && (
                <p className="text-xs text-text-muted">{mentor.mentor_specialty}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
            aria-label="Close modal"
            data-testid="mentor-msg-close"
          >
            <X className="w-5 h-5 text-text-muted" aria-hidden="true" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* 48-hour expectation notice */}
          <div
            className="flex items-start gap-3 p-4 rounded-arch"
            style={{ background: 'rgba(74,173,168,0.08)', border: '1px solid rgba(74,173,168,0.2)' }}
          >
            <Clock className="w-4 h-4 text-accent-teal mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-xs text-text-muted leading-relaxed">
              Mentors typically respond within <strong className="text-text-main">48 hours</strong>. Please be patient and respectful.
            </p>
          </div>

          {sent ? (
            <div
              className="flex items-center gap-3 p-4 rounded-arch"
              style={{ background: 'rgba(74,173,168,0.1)', border: '1px solid rgba(74,173,168,0.3)' }}
              role="status"
              aria-live="polite"
            >
              <Star className="w-5 h-5 text-accent-teal" aria-hidden="true" />
              <p className="text-sm font-medium text-text-main">Message sent! Expect a reply within 48 hours.</p>
            </div>
          ) : (
            <>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
              )}
              <div>
                <label htmlFor="mentor-msg-input" className="block text-sm font-medium text-text-main mb-2">
                  Your message
                </label>
                <div className="relative">
                  <textarea
                    id="mentor-msg-input"
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value.slice(0, MAX_CHARS))}
                    rows={5}
                    placeholder="Share what you'd like guidance on..."
                    className="w-full px-4 py-3 rounded-arch text-sm resize-none focus:ring-2 focus:ring-primary transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.7)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-main)',
                    }}
                    data-testid="mentor-msg-textarea"
                  />
                  <span
                    className="absolute bottom-3 right-4 text-[10px] pointer-events-none"
                    style={{ color: content.length >= MAX_CHARS ? '#dc2626' : 'var(--text-muted)' }}
                    aria-live="polite"
                    aria-label={`${content.length} of ${MAX_CHARS} characters used`}
                  >
                    {content.length}/{MAX_CHARS}
                  </span>
                </div>
                {content.length > 0 && content.length < MIN_CHARS && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    {MIN_CHARS - content.length} more characters needed
                  </p>
                )}
              </div>

              <button
                onClick={handleSend}
                disabled={sending || content.trim().length < MIN_CHARS}
                className="w-full py-3 rounded-arch font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                data-testid="mentor-msg-send-btn"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4" aria-hidden="true" /> Send Message</>
                )}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};


/* ── Mentor Card ── */
const MentorCard = ({ mentor, onMessage }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-6 rounded-arch-lg flex flex-col gap-4"
    style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
    data-testid={`mentor-card-${mentor.id}`}
  >
    <div className="flex items-start gap-4">
      <img
        src={mentor.avatar_url || defaultAvatar(mentor.gender)}
        alt={mentor.display_name}
        className="w-14 h-14 rounded-full object-cover border-2 shrink-0"
        style={{ borderColor: 'var(--primary)' }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-text-main">{mentor.display_name}</span>
          <MentorBadge show withLabel size={11} />
        </div>
        {mentor.mentor_specialty && (
          <div className="flex items-center gap-1.5 mt-1">
            <BookOpen className="w-3.5 h-3.5 text-accent-teal shrink-0" aria-hidden="true" />
            <span className="text-xs text-text-muted">{mentor.mentor_specialty}</span>
          </div>
        )}
      </div>
    </div>

    {mentor.mentor_bio ? (
      <p className="text-sm text-text-muted leading-relaxed">{mentor.mentor_bio}</p>
    ) : (
      <p className="text-sm italic" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>No bio yet.</p>
    )}

    <button
      onClick={() => onMessage(mentor)}
      className="mt-auto w-full py-2.5 rounded-arch text-sm font-medium flex items-center justify-center gap-2 transition-colors"
      style={{ background: 'rgba(74,173,168,0.12)', color: '#2a7b77', border: '1px solid rgba(74,173,168,0.25)' }}
      data-testid={`mentor-msg-btn-${mentor.id}`}
    >
      <Send className="w-4 h-4" aria-hidden="true" />
      Send a Message
    </button>
  </motion.div>
);


/* ════ MENTOR DISCOVERY PAGE ════ */
const MentorDiscoveryPage = ({ onBack, onNavigate }) => {
  const { profile } = useAuth();
  const mentorSignupEnabled = useMentorSignupEnabled();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMentor, setSelectedMentor] = useState(null);

  const isPremium = profile?.is_premium;

  useEffect(() => {
    if (mentorSignupEnabled === null) return;
    if (!isPremium || !mentorSignupEnabled) { setLoading(false); return; }
    api.listMentors()
      .then(r => setMentors(r.data || []))
      .catch(err => setError(err?.response?.data?.detail || 'Could not load mentors.'))
      .finally(() => setLoading(false));
  }, [isPremium, mentorSignupEnabled]);

  if (mentorSignupEnabled === null) {
    return (
      <div className="max-w-3xl mx-auto p-4 lg:p-8 flex items-center justify-center py-24" aria-live="polite" aria-label="Loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (mentorSignupEnabled === false) {
    return (
      <div className="max-w-3xl mx-auto p-4 lg:p-8">
        <div className="rounded-arch-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="p-6 border-b flex items-center gap-4" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={onBack}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
              aria-label="Go back"
              data-testid="mentors-back-btn"
            >
              <ChevronLeft className="w-6 h-6 text-primary" aria-hidden="true" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-600" aria-hidden="true" />
                Mentor Discovery
              </h1>
              <p className="text-sm text-text-muted mt-0.5">Connect with experienced community mentors</p>
            </div>
          </div>
          <div className="text-center py-16 px-6 space-y-4" data-testid="mentors-coming-soon">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ background: 'rgba(169,156,200,0.15)' }}>
              <Sparkles className="w-8 h-8" style={{ color: 'var(--accent-lavender)' }} aria-hidden="true" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-text-main">Coming soon</h2>
            <p className="text-sm text-text-muted max-w-sm mx-auto leading-relaxed">
              We are welcoming our first mentors. Mentor Discovery will open here very soon, so check back shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-3xl mx-auto p-4 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-arch-xl overflow-hidden"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          {/* Header */}
          <div className="p-6 border-b flex items-center gap-4" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={onBack}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
              aria-label="Go back"
              data-testid="mentors-back-btn"
            >
              <ChevronLeft className="w-6 h-6 text-primary" aria-hidden="true" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-600" aria-hidden="true" />
                Mentor Discovery
              </h1>
              <p className="text-sm text-text-muted mt-0.5">Connect with experienced community mentors</p>
            </div>
          </div>

          <div className="p-6">
            {/* Non-premium gate */}
            {!isPremium && (
              <div className="text-center py-16 space-y-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                  style={{ background: 'rgba(169,156,200,0.15)' }}
                >
                  <Sparkles className="w-8 h-8" style={{ color: 'var(--accent-lavender)' }} aria-hidden="true" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-text-main">Sirati+ Required</h2>
                <p className="text-sm text-text-muted max-w-sm mx-auto leading-relaxed">
                  Mentor Discovery is available to Sirati+ members. Upgrade to connect with mentors who can guide your journey.
                </p>
                <button
                  onClick={() => onNavigate?.('premium')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-arch font-medium text-sm transition-colors"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  data-testid="mentors-upgrade-btn"
                >
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  Upgrade to Sirati+
                </button>
              </div>
            )}

            {/* Mentor add-on required (premium but not subscribed) */}
            {isPremium && !profile?.is_mentor && !profile?.is_admin && !profile?.is_mentor_subscriber && !loading && (
              <div
                className="mb-6 p-4 rounded-arch flex items-start gap-3"
                style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)' }}
                role="alert"
              >
                <Star className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-main">Mentor Messaging Add-On Required</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    To send messages to mentors you need the Mentor Messaging add-on ($5/month). You can browse mentors now and subscribe from your profile.
                  </p>
                </div>
                <button
                  onClick={() => onNavigate?.('profile')}
                  className="text-xs font-medium text-yellow-700 hover:underline shrink-0"
                  data-testid="mentors-addon-link"
                >
                  Add On →
                </button>
              </div>
            )}

            {/* Loading */}
            {isPremium && loading && (
              <div className="flex items-center justify-center py-16" aria-live="polite" aria-label="Loading mentors">
                <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
              </div>
            )}

            {/* Error */}
            {isPremium && !loading && error && (
              <p className="text-sm text-red-600 text-center py-8" role="alert">{error}</p>
            )}

            {/* Empty */}
            {isPremium && !loading && !error && mentors.length === 0 && (
              <div className="text-center py-16 text-text-muted">
                <Star className="w-10 h-10 mx-auto mb-3 opacity-30" aria-hidden="true" />
                <p className="text-sm">No mentors available yet. Check back soon.</p>
              </div>
            )}

            {/* Mentor grid */}
            {isPremium && !loading && !error && mentors.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {mentors.map(m => (
                  <MentorCard
                    key={m.id}
                    mentor={m}
                    onMessage={(profile?.is_mentor || profile?.is_mentor_subscriber)
                      ? setSelectedMentor
                      : () => onNavigate?.('profile')}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Messaging modal */}
      <AnimatePresence>
        {selectedMentor && (
          <MentorMessageModal
            mentor={selectedMentor}
            onClose={() => setSelectedMentor(null)}
            onSent={() => setSelectedMentor(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MentorDiscoveryPage;
